import { useGetBrands } from "@entities/brand/lib/hooks/useGetBands";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { Button, Input, Segmented } from "@shared/ui";
import { SelectButton } from "@shared/ui/Select/SelectButton";
import { Flex } from "antd";
import { Download, Plus, Search } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import {
  DOC_TYPE_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  type FinanceInvoiceFiltersProps,
  type InvoiceDocType,
  type InvoiceFiltersState,
} from "./types";
import { MonthDateFilter } from "./ui/MonthDateFilter";

export { DOC_TYPE_OPTIONS, PAYMENT_STATUS_OPTIONS } from "./types";
export type {
  AccountingDateRange,
  FinanceInvoiceFiltersProps,
  InvoiceDocType,
  InvoiceFiltersState,
} from "./types";

const BRAND_LIMIT = 10;
const BRIEF_LIMIT = 10;

export function FinanceInvoiceFilters({
  searchQuery,
  setSearchQuery,
  docType,
  setDocType,
  filters,
  setFilters,
  onExportCsv,
  onAddBilling,
  onAddCreditNote,
  onDownloadBilling,
}: FinanceInvoiceFiltersProps) {
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [briefSearchQuery, setBriefSearchQuery] = useState("");

  const { brands, isFetching: isFetchingBrands } = useGetBrands(
    BRAND_LIMIT,
    1,
    brandSearchQuery
  );
  const { briefs, isFetching: isFetchingBriefs } = useGetBriefs({
    limit: BRIEF_LIMIT,
    pageNo: 1,
    searchQuery: briefSearchQuery,
  });

  const brandLabelCache = useRef<Record<string, string>>({});
  const briefLabelCache = useRef<Record<string, string>>({});

  const brandOptions = useMemo(() => {
    const fromApi = (brands?.data ?? []).map((b) => {
      brandLabelCache.current[b._id] = b.name;
      return { value: b._id, label: b.name };
    });
    const selectedNotInList = filters.brandIds.filter(
      (b) => !fromApi.some((o) => o.value === b.id)
    );
    const selectedOptions = selectedNotInList.map((b) => ({
      value: b.id,
      label: brandLabelCache.current[b.id] ?? b.name,
    }));
    return [...selectedOptions, ...fromApi];
  }, [brands, filters.brandIds]);

  const briefOptions = useMemo(() => {
    const fromApi = (briefs ?? []).map((b) => {
      briefLabelCache.current[b._id] = b.name;
      return { value: b._id, label: b.name };
    });
    const selectedNotInList = filters.briefIds.filter(
      (b) => !fromApi.some((o) => o.value === b.id)
    );
    const selectedOptions = selectedNotInList.map((item) => ({
      value: item.id,
      label: briefLabelCache.current[item.id] ?? item.name,
    }));
    return [...selectedOptions, ...fromApi];
  }, [briefs, filters.briefIds]);

  const updateAccountingDate = (
    range: InvoiceFiltersState["accountingDate"]
  ) => {
    setFilters((prev) => ({ ...prev, accountingDate: range }));
  };

  const updateFilter = <K extends keyof InvoiceFiltersState>(
    key: K,
    value: InvoiceFiltersState[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      {/* Top Row: Search + Segment (doc type) */}
      <div className={styles.topRow}>
        <div className={styles.searchWrap}>
          <Input
            placeholder="Search by document #, reference or campaign..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            prefix={<Search size={16} className={styles.searchIcon} />}
            className={styles.searchInput}
            allowClear
            style={{ width: 300 }}
          />
        </div>
        <div className={styles.segmentWrap}>
          <Segmented
            value={docType}
            onChange={(value) => setDocType(value as InvoiceDocType)}
            options={DOC_TYPE_OPTIONS}
            className={styles.segment}
          />
        </div>
      </div>

      {/* Bottom Row: Filters + Actions */}
      <Flex className={styles.bottomRow}>
        <div className={styles.filters}>
          <SelectButton
            mode="multiple"
            withFooterActions
            searchable
            value={filters.brandIds?.map((b) => b.id) ?? []}
            onChange={(v) =>
              updateFilter(
                "brandIds",
                (v ?? []).map((id: string) => ({
                  id,
                  name: brandLabelCache.current[id] ?? "",
                }))
              )
            }
            options={brandOptions}
            placeholder="Brands"
            label="Brands"
            searchPlaceholder="Search brands..."
            onSearchChange={setBrandSearchQuery}
            searchValue={brandSearchQuery}
            className={styles.select}
            style={{ minWidth: 120 }}
            isLoading={isFetchingBrands}
          />
          <SelectButton
            mode="multiple"
            searchable
            withFooterActions
            value={filters.briefIds?.map((b) => b.id)}
            onChange={(v) =>
              updateFilter(
                "briefIds",
                (v ?? []).map((id: string) => ({
                  id,
                  name: briefLabelCache.current[id] ?? "",
                }))
              )
            }
            options={briefOptions}
            placeholder="Campaigns"
            label="Campaigns"
            searchPlaceholder="Search campaigns..."
            onSearchChange={setBriefSearchQuery}
            searchValue={briefSearchQuery}
            className={styles.select}
            style={{ minWidth: 160 }}
            isLoading={isFetchingBriefs}
          />
          <MonthDateFilter
            value={filters.accountingDate}
            onChange={updateAccountingDate}
            placeholder="Month"
          />
          <SelectButton
            mode="multiple"
            value={filters.paymentStatuses}
            onChange={(v) =>
              updateFilter(
                "paymentStatuses",
                (v ?? []) as InvoiceFiltersState["paymentStatuses"]
              )
            }
            options={PAYMENT_STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            placeholder="Payment Status"
            label="Payment Status"
            className={styles.select}
            style={{ minWidth: 140 }}
          />
        </div>

        <Flex wrap gap={12}>
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={onAddBilling}
            className={styles.btnPrimary}
          >
            Add
          </Button>
          {/* <Button
                type="default"
                icon={<FileMinus size={16} />}
                onClick={onAddCreditNote}
              >
                Add Credit Note
              </Button> */}

          <Button
            type="default"
            icon={<Download size={16} />}
            onClick={onDownloadBilling}
          >
            Download
          </Button>

          <Button
            type="default"
            icon={<Download size={16} />}
            onClick={onExportCsv}
          >
            Export CSV
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
